import { getFSRSDueColor } from "@components/fsrsDue";
import { getFSRSDueStatus } from "@components/fsrsDueStatus";
import MiddleEllipse from "@components/middleEllipse";
import ProgressPie from "@components/progressPie";
import Subtitle from "@components/subtitle";
import { knolToFSRSCard } from "@core/fsrs/scheduler";
import { Deck } from "@models/deck";
import { TimerMode } from "@models/deckSettings";
import { IKnol } from "@models/knol";
import L10n from "localization";
import { useEffect, useRef, useState } from "react";
import Style from "style";
import TimerHUD from "./reviewTimer";
import TagHUD from "./tagHUD";

interface IProps {
  deckName: string;
  deckTagName: string | undefined;
  activeCardIndex: number;
  numCards: number;
  showTags: boolean;
  cardTags: string[];
  timerMillis?: number;
  isTheaterMode?: boolean;
  timerMode: TimerMode;
  currentKnol?: IKnol;
  isFSRS?: boolean;
}
export default function HUD(props: IProps): JSX.Element {
  const {
    deckName,
    deckTagName,
    activeCardIndex,
    numCards,
    showTags,
    cardTags,
    timerMillis,
    isTheaterMode,
    timerMode,
    currentKnol,
    isFSRS,
  } = props;
  const hudStyle: React.CSSProperties = {
    position: "fixed",
    zIndex: 9,
    width: "100%",
    opacity: 0.9,
  };

  const showTimer = timerMode !== "off" && timerMillis !== undefined;
  const displayName = Deck.folderSeparatorFormattedName(deckName ?? "");
  const progress = activeCardIndex / numCards || 0;

  // State to handle proper positioning after render to prevent jumping
  const [isPositioned, setIsPositioned] = useState(false);

  // Cache the FSRS status to prevent it from flipping to "New" when tags are modified
  const fsrsStatusCacheRef = useRef<{ knolId: string; status: string; color: string } | null>(null);

  // Effect to handle initial positioning delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPositioned(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Effect to clear cache when switching cards
  useEffect(() => {
    const currentKnolId = currentKnol?.id;
    const cachedStatus = fsrsStatusCacheRef.current;

    // Clear cache if we're on a different card
    if (cachedStatus && cachedStatus.knolId !== currentKnolId) {
      fsrsStatusCacheRef.current = null;
    }
  }, [currentKnol?.id]);

  // Get FSRS status for current card
  let fsrsStatus: string | null = null;
  let statusColor: string | null = null;
  if (isFSRS && currentKnol) {
    try {
      const fsrsCard = knolToFSRSCard(currentKnol);
      if (fsrsCard) {
        // Check if we have a cached status for this knol and if the card incorrectly shows as "New"
        // This prevents the status from flipping to "New" when tags are modified
        const cachedStatus = fsrsStatusCacheRef.current;
        const isIncorrectlyNew =
          fsrsCard.state === "New" &&
          cachedStatus &&
          cachedStatus.knolId === currentKnol.id &&
          cachedStatus.status !== L10n.localize((s) => s.fsrs.stateNew).toUpperCase();

        if (isIncorrectlyNew) {
          // Use cached status to prevent incorrect "New" state
          fsrsStatus = cachedStatus.status;
          statusColor = cachedStatus.color;
        } else if (fsrsCard.state === "New") {
          fsrsStatus = L10n.localize((s) => s.fsrs.stateNew).toUpperCase();
          statusColor = Style.colors.grades.NEW;
        } else {
          const status = getFSRSDueStatus(fsrsCard.due);
          statusColor = getFSRSDueColor(fsrsCard.due);

          // Map status to display labels
          switch (status) {
            case "Now":
              fsrsStatus = L10n.localize((s) => s.fsrs.dueNow).toUpperCase();
              break;
            case "Within24hr":
              fsrsStatus = L10n.localize((s) => s.fsrs.dueWithin24hr).toUpperCase();
              break;
            case "Tomorrow":
              fsrsStatus = L10n.localize((s) => s.general.tomorrow).toUpperCase();
              break;
            case "Within1wk":
              fsrsStatus = L10n.localize((s) => s.fsrs.dueWithin1wk).toUpperCase();
              break;
            case "Future":
              fsrsStatus = L10n.localize((s) => s.general.future).toUpperCase();
              break;
          }
        }

        // Cache the status for this knol (but only if it's not "New" or if it's legitimately new)
        if (fsrsStatus && statusColor && (!isIncorrectlyNew || fsrsCard.state !== "New")) {
          fsrsStatusCacheRef.current = {
            knolId: currentKnol.id,
            status: fsrsStatus,
            color: statusColor,
          };
        }
      }
    } catch (error) {
      console.warn("Failed to get FSRS status:", error);
    }
  }

  return (
    <>
      <div style={hudStyle}>
        <Subtitle
          text={isTheaterMode ? undefined : <MiddleEllipse text={displayName} />}
          progress={progress}
          isTheaterMode={isTheaterMode}
        />
        {/* FSRS status positioned within subtitle area for normal mode */}
        {!isTheaterMode && fsrsStatus && statusColor && (
          <div
            style={{
              position: "absolute",
              left: 16,
              top: 4,
              zIndex: 1000,
              padding: "5px 6px",
              borderRadius: "3px",
              backgroundColor: statusColor,
              color: "#fff",
              fontSize: "10px",
              fontWeight: 600,
              textTransform: "uppercase",
              opacity: isPositioned ? 0.9 : 0,
              boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
              transition: "opacity 0.15s ease-in-out",
            }}
          >
            {fsrsStatus}
          </div>
        )}
      </div>
      {showTimer && <TimerHUD isTheaterMode={isTheaterMode} />}
      {showTags && (
        <TagHUD
          folder={deckTagName}
          tags={cardTags}
          noChip
          style={{
            left: showTimer ? 32 : 8,
            marginTop: isTheaterMode ? 4 : 36,
          }}
        />
      )}
      {/* FSRS status for theater mode - positioned below header */}
      {isTheaterMode && fsrsStatus && statusColor && (
        <div
          style={{
            position: "absolute", // NOTE: when position was "fixed" this badge would jump into place.
            left: 16,
            top: `calc(env(safe-area-inset-top, 0px) + ${Style.headerHeightPx}px + 8px)`,
            zIndex: 999,
            padding: "5px 6px",
            borderRadius: "3px",
            backgroundColor: statusColor,
            color: "#fff",
            fontSize: "10px",
            fontWeight: 600,
            textTransform: "uppercase",
            opacity: isPositioned ? 0.6 : 0,
            boxShadow: "0 1px 2px rgba(0,0,0,0.3)",
            transition: "opacity 0.15s ease-in-out",
          }}
        >
          {fsrsStatus}
        </div>
      )}
      {isTheaterMode ? (
        <ProgressPie
          donut={isTheaterMode}
          progress={progress}
          style={{
            opacity: 0.5,
            position: "fixed",
            right: "5px",
            zIndex: 9,
          }}
        />
      ) : undefined}
    </>
  );
}
